import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Calendar, Sparkles, FileText } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

// Translations for the CTA
const ctaTranslations = {
  nl: {
    headline: 'Klaar om online te groeien?',
    subtext: 'Vraag vandaag een gratis gesprek aan.',
    button: 'Plan een kennismaking',
    quoteButton: 'Gratis offerte aanvragen',
    dismiss: 'Sluiten'
  },
  en: {
    headline: 'Ready to grow online?',
    subtext: 'Request a free consultation today.',
    button: 'Schedule a meeting',
    quoteButton: 'Request a free quote',
    dismiss: 'Close'
  },
  fr: {
    headline: 'Prêt à grandir en ligne?',
    subtext: 'Demandez une consultation gratuite aujourd\'hui.',
    button: 'Planifier un rendez-vous',
    quoteButton: 'Devis gratuit',
    dismiss: 'Fermer'
  },
  de: {
    headline: 'Bereit online zu wachsen?',
    subtext: 'Fordern Sie heute ein kostenloses Gespräch an.',
    button: 'Termin vereinbaren',
    quoteButton: 'Kostenloses Angebot',
    dismiss: 'Schließen'
  }
};

export const StickyCTA = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [hasScrolled, setHasScrolled] = useState(false);
  
  const t = ctaTranslations[language] || ctaTranslations.nl;
  const contactPath = getLocalizedPath('contact', language);
  
  // Don't show on contact page or admin pages
  const isContactPage = location.pathname.includes('/contact') || location.pathname.includes('/kontakt');
  const isAdminPage = location.pathname.includes('-admin') || location.pathname.includes('/admin');
  
  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Don't render on contact or admin pages
  if (isContactPage || isAdminPage) return null;
  
  return (
    <>
      {/* Desktop Sidebar CTA - Fixed on right side */}
      <AnimatePresence>
        {isVisible && hasScrolled && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
            className="hidden lg:block fixed right-6 top-[35%] -translate-y-1/2 z-40"
            data-testid="desktop-sticky-cta"
          >
            <div className="relative bg-white rounded-2xl shadow-[0_20px_50px_rgba(37,99,235,0.15)] border border-gray-200 p-6 w-[280px]">
              {/* Close button */}
              <button
                onClick={() => setIsVisible(false)}
                className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-md border border-gray-200 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                aria-label={t.dismiss}
              >
                <X size={16} />
              </button>
              
              {/* Sparkle decoration */}
              <div className="absolute -top-3 -left-3 w-10 h-10 bg-[#2563EB] rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles size={20} className="text-white" />
              </div>
              
              {/* Content */}
              <div className="pt-4">
                <h3 className="font-bold text-[#111827] text-lg mb-2">
                  {t.headline}
                </h3>
                <p className="text-[#4B5563] text-sm mb-5">
                  {t.subtext}
                </p>
                
                {/* CTA Button */}
                <Link
                  to={contactPath}
                  className="group flex items-center justify-center gap-2 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-semibold py-3 px-4 rounded-xl shadow-[0_4px_15px_rgba(37,99,235,0.3)] hover:shadow-[0_6px_20px_rgba(37,99,235,0.4)] transition-all duration-300"
                  data-testid="desktop-cta-button"
                >
                  <Calendar size={18} />
                  {t.button}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Mobile Sticky Bottom Bar - Quote button within thumb reach */}
      <AnimatePresence>
        {isVisible && hasScrolled && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ type: 'spring', stiffness: 100, damping: 20 }}
            className="lg:hidden fixed bottom-0 left-0 right-0 z-40 px-3 pb-3 pt-0 safe-area-bottom"
            data-testid="mobile-sticky-cta"
          >
            <div className="flex items-center gap-2">
              {/* Primary quote button - opens chatbot quote calculator directly */}
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('netblitz:open-quote'))}
                className="group flex-1 flex items-center justify-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] active:bg-[#1E40AF] text-white font-bold text-base py-4 px-5 rounded-2xl cta-pulse transition-all duration-300 min-h-[56px]"
                data-testid="mobile-quote-cta-button"
              >
                <FileText size={20} />
                {t.quoteButton}
                <ArrowRight size={18} className="group-active:translate-x-1 transition-transform" />
              </button>

              {/* Close button - Touch friendly */}
              <button
                onClick={() => setIsVisible(false)}
                className="flex-shrink-0 w-11 h-14 bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-md flex items-center justify-center text-slate-400 hover:text-slate-600 active:bg-slate-100 transition-colors"
                aria-label={t.dismiss}
                data-testid="mobile-cta-dismiss"
              >
                <X size={20} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
